/**
 * AM-026 — Password Change/Reset and User Permission Override API Hooks.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance as apiClient } from "@/api/client";

// Types
export interface ChangeMyPasswordPayload {
  old_password: string;
  new_password: string;
}

export interface ResetPasswordResponse {
  generated_password: string;
  must_change_password: boolean;
}

export type PermissionOverrideEffect = "grant" | "deny" | "none";

export interface EffectivePermissionRow {
  permission_code: string;
  label: string;
  from_role: boolean;
  override: PermissionOverrideEffect;
  effective: boolean;
}

export interface UpdatePermissionsPayload {
  overrides: Array<{ permission_code: string; effect: PermissionOverrideEffect }>;
}

// Hooks
export function useChangeMyPassword() {
  return useMutation({
    mutationFn: async (payload: ChangeMyPasswordPayload) => {
      await apiClient.post("/api/v1/users/me/change-password/", payload);
    },
  });
}

export function useResetUserPassword(userId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<ResetPasswordResponse>(
        `/api/v1/users/${userId}/reset-password/`,
      );
      return data;
    },
  });
}

export function useUserPermissions(userId: string) {
  return useQuery({
    queryKey: ["user-permissions", userId],
    queryFn: async () => {
      const { data } = await apiClient.get<EffectivePermissionRow[]>(
        `/api/v1/users/${userId}/permissions/`,
      );
      return data;
    },
    enabled: Boolean(userId),
  });
}

export function useUpdateUserPermissions(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdatePermissionsPayload) => {
      const { data } = await apiClient.post<EffectivePermissionRow[]>(
        `/api/v1/users/${userId}/permissions/`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-permissions", userId] });
    },
  });
}
